'use client';

import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import {
  Box,
  Typography,
  Card,
  CardContent,
  CardActions,
  Button,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Alert,
  CircularProgress,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  Checkbox,
  Menu,
  MenuItem,
  Toolbar,
  alpha
} from '@mui/material';
import {
  CloudDownload as DownloadIcon,
  Delete as DeleteIcon,
  Description as FileIcon,
  PictureAsPdf as PdfIcon,
  InsertDriveFile as DocIcon,
  TableChart as ExcelIcon,
  Slideshow as PptIcon,
  TextSnippet as TxtIcon,
  Image as ImageIcon,
  Refresh as RefreshIcon,
  Visibility as ViewIcon,
  Summarize as ExtractIcon,
  ListAlt as ViewLogsIcon,
  AutoAwesome as AutoAwesomeIcon,
  Assessment as TaskStatusIcon,
  MoreVert as MoreVertIcon,
  PlaylistAddCheck as BatchIcon,
  Storage as StorageIcon,
  Sync as SyncIcon,
  CloudSync as CloudSyncIcon,
  BubbleChart as GraphIcon
} from '@mui/icons-material';
import { useParams, useNavigate } from 'react-router-dom';
import { apiClient } from '@/services/api';
import Navbar from '@/components/home/Navbar';
import DocumentUploadDialog from '@/components/upload/DocumentUploadDialog';
import { useAuth } from '@/contexts/AuthContext';

interface Document {
  id: number;
  project_id: number;
  document_name: string;
  document_path: string;
  document_type: string;
  file_size: number;
  status: string;
  uploaded_at: string;
  updated_at: string;
  markdown_path?: string;
  metadata?: Array<{name: string, value: string}>;
}

const DocumentsPage: React.FC = () => {
  const { projectId } = useParams<{ projectId: string }>();
  const navigate = useNavigate();
  const { user: currentUser } = useAuth();
  const [documents, setDocuments] = useState<Document[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState<boolean>(false);
  const [markdownDialogOpen, setMarkdownDialogOpen] = useState<boolean>(false);
  const [selectedDocument, setSelectedDocument] = useState<Document | null>(null);
  const [markdownContent, setMarkdownContent] = useState<string>('');
  const [loadingMarkdown, setLoadingMarkdown] = useState<boolean>(false);
  const [extractingLog, setExtractingLog] = useState<boolean>(false);
  const [logsDialogOpen, setLogsDialogOpen] = useState<boolean>(false);
  const [projectLogs, setProjectLogs] = useState<string>('');
  const [loadingLogs, setLoadingLogs] = useState<boolean>(false);
  const [extractConfirmDialogOpen, setExtractConfirmDialogOpen] = useState<boolean>(false);
  const [extractLogConfirmDialogOpen, setExtractLogConfirmDialogOpen] = useState<boolean>(false);
  const [extractedLogDialogOpen, setExtractedLogDialogOpen] = useState<boolean>(false);
  const [extractedLogContent, setExtractedLogContent] = useState<string>('');
  const [logExtractionTaskId, setLogExtractionTaskId] = useState<string | null>(null);
  const logExtractionPollingIntervalRef = useRef<NodeJS.Timeout | null>(null);

  // 任务状态相关状态
  const [taskStatusDialogOpen, setTaskStatusDialogOpen] = useState<boolean>(false);
  const [taskStatusData, setTaskStatusData] = useState<any>(null);
  const [loadingTaskStatus, setLoadingTaskStatus] = useState<boolean>(false);
  const [documentTaskStatus, setDocumentTaskStatus] = useState<{[key: number]: any}>({});
  const [loadingInitialTaskStatus, setLoadingInitialTaskStatus] = useState<boolean>(false);

  // 日志提取任务状态
  const [logExtractionTaskStatus, setLogExtractionTaskStatus] = useState<any>(null);
  const [loadingLogTaskStatus, setLoadingLogTaskStatus] = useState<boolean>(false);

  // 批量操作相关状态
  const [selectedDocuments, setSelectedDocuments] = useState<Set<number>>(new Set());
  const [batchMenuAnchorEl, setBatchMenuAnchorEl] = useState<null | HTMLElement>(null);
  const [batchDeleteDialogOpen, setBatchDeleteDialogOpen] = useState<boolean>(false);
  const [batchReprocessDialogOpen, setBatchReprocessDialogOpen] = useState<boolean>(false);
  const [batchProcessing, setBatchProcessing] = useState<boolean>(false);
  const [indeterminate, setIndeterminate] = useState<boolean>(false);

  // 上传弹框状态
  const [uploadDialogOpen, setUploadDialogOpen] = useState<boolean>(false);

  // cognee重建状态
  const [syncingRAG, setSyncingRAG] = useState<boolean>(false);
  const [ragSyncStatus, setRagSyncStatus] = useState<any>(null);
  const [ragConfirmDialogOpen, setRagConfirmDialogOpen] = useState<boolean>(false);

  // 图数据库可视化状态
  const [graphVisualizationDialogOpen, setGraphVisualizationDialogOpen] = useState<boolean>(false);
  const [graphVisualizationHtml, setGraphVisualizationHtml] = useState<string>('');
  const [loadingGraphVisualization, setLoadingGraphVisualization] = useState<boolean>(false);

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  // 确保documents是数组，并使用useMemo优化
  const safeDocuments = useMemo(() => Array.isArray(documents) ? documents : [], [documents]);

  const formatDate = (dateString: string): string => {
    return new Date(dateString).toLocaleString('zh-CN');
  };

  // 深度比较两个任务状态是否相同
  const isTaskStatusEqual = useCallback((status1: any, status2: any): boolean => {
    if (!status1 && !status2) return true;
    if (!status1 || !status2) return false;

    return (
      status1.status === status2.status &&
      status1.progress === status2.progress &&
      status1.progress_message === status2.progress_message &&
      status1.task_id === status2.task_id &&
      status1.created_at === status2.created_at &&
      status1.completed_at === status2.completed_at
    );
  }, []);

  // 根据任务列表获取每个文档的最新任务状态
  const getLatestTaskStatusForDocuments = useCallback((tasks: any[]) => {
    const docStatusMap: {[key: number]: any} = {};

    // 首先按文档ID分组所有任务
    const tasksByDocument: {[key: number]: any[]} = {};
    tasks.forEach((task: any) => {
      if (task.document_id) {
        if (!tasksByDocument[task.document_id]) {
          tasksByDocument[task.document_id] = [];
        }
        tasksByDocument[task.document_id].push(task);
      }
    });

    // 然后为每个文档选择最合适的任务状态
    Object.keys(tasksByDocument).forEach(docId => {
      const docIdNum = parseInt(docId);
      const tasks = tasksByDocument[docIdNum];

      if (tasks.length > 0) {
        // 优先选择正在运行的任务，如果没有运行中的任务，则选择最新的任务
        const runningTasks = tasks.filter((task: any) => task.status === 'running');
        const pendingTasks = tasks.filter((task: any) => task.status === 'pending');

        let selectedTask: any;
        if (runningTasks.length > 0) {
          // 如果有运行中的任务，选择最新开始的运行任务
          selectedTask = runningTasks.sort((a: any, b: any) => {
            const timeA = new Date(a.created_at || 0).getTime();
            const timeB = new Date(b.created_at || 0).getTime();
            return timeB - timeA;
          })[0];
        } else if (pendingTasks.length > 0) {
          // 如果有等待中的任务，选择最新的等待任务
          selectedTask = pendingTasks.sort((a: any, b: any) => {
            const timeA = new Date(a.created_at || 0).getTime();
            const timeB = new Date(b.created_at || 0).getTime();
            return timeB - timeA;
          })[0];
        } else {
          // 否则选择最新的任务（按状态优先级：失败 > 已完成 > 已取消）
          const statusPriority = {
            'failed': 3,
            'completed': 2,
            'cancelled': 1,
            'pending': 0,
            'running': 4
          };

          selectedTask = tasks.sort((a: any, b: any) => {
            // 首先按状态优先级排序
            const priorityDiff = (statusPriority[b.status] || 0) - (statusPriority[a.status] || 0);
            if (priorityDiff !== 0) {
              return priorityDiff;
            }
            // 状态相同时按时间排序，最新的在前
            const timeA = new Date(a.created_at || 0).getTime();
            const timeB = new Date(b.created_at || 0).getTime();
            return timeB - timeA;
          })[0];
        }

        docStatusMap[docIdNum] = selectedTask;
      }
    });

    return docStatusMap;
  }, []);

  // 创建一个完全独立的任务状态组件，避免依赖父组件状态
  const TaskStatusRenderer = React.memo<{ taskStatus: any }>(({ taskStatus }) => {
    if (!taskStatus) {
      return <Typography variant="body2" color="text.secondary">-</Typography>;
    }

    const getStatusColor = useCallback((status: string) => {
      switch (status) {
        case 'completed': return 'success';
        case 'running': return 'info';
        case 'failed': return 'error';
        case 'pending': return 'default';
        case 'cancelled': return 'default';
        default: return 'default';
      }
    }, []);

    const getStatusText = useCallback((status: string) => {
      switch (status) {
        case 'completed': return '已完成';
        case 'running': return '运行中';
        case 'failed': return '失败';
        case 'pending': return '等待中';
        case 'cancelled': return '已取消';
        default: return status;
      }
    }, []);

    // 使用 useMemo 缓存渲染内容，只有状态真正变化时才重新渲染
    const statusContent = useMemo(() => {
      return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 120 }}>
          <Chip
            label={getStatusText(taskStatus.status)}
            color={getStatusColor(taskStatus.status) as any}
            size="small"
          />
          {taskStatus.status === 'running' && (
            <CircularProgress size={16} />
          )}
          {taskStatus.progress > 0 && (
            <Typography variant="caption" color="text.secondary">
              {taskStatus.progress}%
            </Typography>
          )}
        </Box>
      );
    }, [taskStatus.status, taskStatus.progress, getStatusColor, getStatusText]);

    return statusContent;
  }, (prevProps, nextProps) => {
    // 深度比较任务状态，只有真正变化时才重新渲染
    const prev = prevProps.taskStatus;
    const next = nextProps.taskStatus;

    if (!prev && !next) return true;
    if (!prev || !next) return false;

    return (
      prev.status === next.status &&
      prev.progress === next.progress &&
      prev.progress_message === next.progress_message &&
      prev.task_id === next.task_id
    );
  });

  // 优化的表格行组件
  const DocumentTableRow = React.memo<{
    document: Document;
    isSelected: boolean;
    onSelectDocument: (id: number) => void;
    onDownload: (doc: Document) => void;
    onViewMarkdown: (doc: Document) => void;
    onDeleteClick: (doc: Document) => void;
    taskStatus: any;
  }>(({ document, isSelected, onSelectDocument, onDownload, onViewMarkdown, onDeleteClick, taskStatus }) => {
    // 缓存文件图标
    const fileIcon = useMemo(() => getFileIcon(document.document_type), [document.document_type]);

    // 缓存格式化后的文件大小
    const formattedFileSize = useMemo(() => formatFileSize(document.file_size), [document.file_size]);

    // 缓存格式化后的时间
    const formattedDate = useMemo(() => formatDate(document.uploaded_at), [document.uploaded_at]);

    return (
      <TableRow
        key={document.id}
        hover
        selected={isSelected}
        sx={{
          '&.Mui-selected': {
            backgroundColor: (theme) => alpha(theme.palette.primary.main, 0.08),
            '&:hover': {
              backgroundColor: (theme) => alpha(theme.palette.primary.main, 0.12),
            }
          }
        }}
      >
        <TableCell padding="checkbox">
          <Checkbox
            checked={isSelected}
            onChange={() => onSelectDocument(document.id)}
          />
        </TableCell>
        <TableCell>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            {fileIcon}
            <Typography variant="body2" sx={{ fontWeight: 500 }}>
              {document.document_name}
            </Typography>
          </Box>
        </TableCell>
        <TableCell>
          <Chip
            label={document.document_type ? document.document_type.toUpperCase() : 'UNKNOWN'}
            size="small"
            variant="outlined"
          />
        </TableCell>
        <TableCell>{formattedFileSize}</TableCell>
        <TableCell>
          <Chip
            label={getStatusText(document.status)}
            color={getStatusColor(document.status)}
            size="small"
          />
        </TableCell>
        <TableCell>
          <TaskStatusRenderer
            key={`${document.id}-${taskStatus?.task_id || 'no-task'}`}
            taskStatus={taskStatus}
          />
        </TableCell>
        <TableCell>{formattedDate}</TableCell>
        <TableCell align="right">
          {document.status === 'uploaded' && (
            <Tooltip title="查看文档内容">
              <IconButton
                size="small"
                onClick={() => onViewMarkdown(document)}
                sx={{ mr: 1 }}
                color="primary"
              >
                <ViewIcon />
              </IconButton>
            </Tooltip>
          )}
          <Tooltip title="下载">
            <IconButton
              size="small"
              onClick={() => onDownload(document)}
              sx={{ mr: 1 }}
            >
              <DownloadIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="删除">
            <IconButton
              size="small"
              onClick={() => onDeleteClick(document)}
              color="error"
            >
              <DeleteIcon />
            </IconButton>
          </Tooltip>
        </TableCell>
      </TableRow>
    );
  }, (prevProps, nextProps) => {
    // 比较文档属性和选中状态
    const prevDoc = prevProps.document;
    const nextDoc = nextProps.document;

    // 简单比较：如果文档ID、状态、选中状态和任务状态都没变，就不重新渲染
    return (
      prevDoc.id === nextDoc.id &&
      prevDoc.status === nextDoc.status &&
      prevProps.isSelected === nextProps.isSelected &&
      JSON.stringify(prevProps.taskStatus) === JSON.stringify(nextProps.taskStatus)
    );
  });

  
  const getFileIcon = (fileType: string) => {
    if (!fileType) return <FileIcon sx={{ color: '#616161' }} />;
    const type = fileType.toLowerCase();
    if (type === '.pdf') return <PdfIcon sx={{ color: '#d32f2f' }} />;
    if (type === '.docx') return <DocIcon sx={{ color: '#1976d2' }} />;
    if (type === '.xls' || type === '.xlsx') return <ExcelIcon sx={{ color: '#388e3c' }} />;
    if (type === '.ppt' || type === '.pptx') return <PptIcon sx={{ color: '#f57c00' }} />;
    if (type === '.txt' || type === '.md') return <TxtIcon sx={{ color: '#616161' }} />;
    if (type === '.png' || type === '.jpg' || type === '.jpeg') return <ImageIcon sx={{ color: '#7b1fa2' }} />;
    return <FileIcon sx={{ color: '#616161' }} />;
  };

  const getStatusColor = (status: string): 'default' | 'success' | 'warning' | 'error' => {
    switch (status) {
      case 'uploaded':
        return 'success';
      case 'processing':
        return 'warning';
      case 'failed':
        return 'error';
      default:
        return 'default';
    }
  };

  const getStatusText = (status: string): string => {
    switch (status) {
      case 'uploaded':
        return '已上传';
      case 'processing':
        return '处理中';
      case 'failed':
        return '失败';
      default:
        return status;
    }
  };

  const fetchDocuments = async (skipLoading = false) => {
    if (!projectId) return;

    if (!skipLoading) {
      setLoading(true);
    }
    setError(null);

    try {
      console.log(`fetchDocuments: 获取项目 ${projectId} 的文档列表，skipLoading=${skipLoading}`);
      const response = await apiClient.documents.getList(projectId);
      if (response.success) {
        const newDocuments = Array.isArray(response.data?.documents) ? response.data.documents : [];
        setDocuments(newDocuments);
        console.log(`fetchDocuments: 成功获取 ${newDocuments.length} 个文档`);
      } else {
        console.error('fetchDocuments: API返回失败', response.message);
        setError(response.message || '获取文档列表失败');
      }
    } catch (err) {
      console.error('fetchDocuments: 获取文档列表失败:', err);
      setError('获取文档列表失败，请稍后重试');
    } finally {
      if (!skipLoading) {
        setLoading(false);
      }
    }
  };

  const handleDownload = async (document: Document) => {
    try {
      // 使用apiClient的download方法获取文件，确保包含认证头
      const filename = document.document_path.split('/').pop();
      console.log('下载文件:', { filename, document, projectId });

      // 检查token是否存在
      const token = localStorage.getItem('auth_token');
      console.log('认证token:', token ? '存在' : '不存在');

      const response = await apiClient.documents.download(projectId, filename);
      console.log('下载响应:', response);

      // apiClient的download直接返回blob数据
      const blob = response;
      const url = URL.createObjectURL(blob);

      // 创建下载链接，确保document对象存在
      if (typeof document !== 'undefined' && document.createElement) {
        const link = document.createElement('a');
        link.href = url;
        link.download = document.document_name;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        // 清理blob URL
        URL.revokeObjectURL(url);
      } else {
        // 如果document对象不可用，提供直接下载链接
        window.open(url, '_blank');
        setTimeout(() => URL.revokeObjectURL(url), 100);
      }
    } catch (err) {
      console.error('下载失败:', err);
    }
  };

  const handleDelete = async () => {
    if (!selectedDocument || !projectId) return;

    try {
      const response = await apiClient.documents.delete(projectId, selectedDocument.id.toString());
      if (response.success) {
        setDocuments(prev => Array.isArray(prev) ? prev.filter(doc => doc.id !== selectedDocument.id) : []);
      }
    } catch (err) {
      console.error('删除失败:', err);
    } finally {
      setDeleteDialogOpen(false);
      setSelectedDocument(null);
    }
  };

  const handleDeleteClick = (document: Document) => {
    setSelectedDocument(document);
    setDeleteDialogOpen(true);
  };

  const handleViewMarkdown = async (document: Document) => {
    if (!document.markdown_path) {
      return;
    }

    setLoadingMarkdown(true);
    setMarkdownDialogOpen(true);
    setSelectedDocument(document);

    try {
      // 从markdown_path中提取文件名
      const markdownFilename = document.markdown_path.split('/').pop();
      const response = await apiClient.documents.getMarkdown(projectId, markdownFilename);

      if (response.success) {
        // getMarkdown返回的是直接的markdown内容
        setMarkdownContent(response.data);
      } else {
        setMarkdownContent('无法加载Markdown内容');
      }
    } catch (err) {
      console.error('加载Markdown内容失败:', err);
      setMarkdownContent('加载Markdown内容时发生错误');
    } finally {
      setLoadingMarkdown(false);
    }
  };

  
  const handleExtractProjectLog = () => {
    if (!projectId) return;

    // 检查是否有已上传的文档
    const uploadedDocuments = safeDocuments.filter(doc => doc.status === 'uploaded');
    if (uploadedDocuments.length === 0) {
      setError('请先上传文档后再提取日志');
      return;
    }

    // 显示确认对话框
    setExtractLogConfirmDialogOpen(true);
  };

  const handleExtractLogConfirmed = async () => {
    if (!projectId) return;

    setExtractingLog(true);
    setError(null);
    setExtractLogConfirmDialogOpen(false);
    setLogExtractionTaskId(null);

    try {
      const response = await apiClient.documents.extractLog(projectId);
      
      if (response.success && response.data?.task_id) {
        setError(null);
        setLogExtractionTaskId(response.data.task_id);
        // 开始轮询任务状态
        startLogExtractionPolling(response.data.task_id);
      } else {
        setError(response.message || '创建项目日志提取任务失败');
        setExtractingLog(false);
      }
    } catch (err: any) {
      console.error('创建项目日志提取任务失败:', err);
      setError(err.response?.data?.detail || '创建项目日志提取任务时发生错误');
      setExtractingLog(false);
    }
  };

  // 开始轮询日志提取任务状态
  const startLogExtractionPolling = useCallback((taskId: string) => {
    // 清除之前的轮询
    if (logExtractionPollingIntervalRef.current) {
      clearInterval(logExtractionPollingIntervalRef.current);
    }

    // 立即检查一次
    checkLogExtractionTaskStatus(taskId);

    // 每2秒轮询一次
    const interval = setInterval(() => {
      checkLogExtractionTaskStatus(taskId);
    }, 2000);

    logExtractionPollingIntervalRef.current = interval;
  }, [projectId]);

  // 检查日志提取任务状态
  const checkLogExtractionTaskStatus = useCallback(async (taskId: string) => {
    if (!projectId) return;

    try {
      const response = await apiClient.documentTasks.getStatus(projectId);
      
      if (response.success && response.data?.tasks) {
        // 查找对应的任务
        const task = response.data.tasks.find((t: any) => t.task_id === taskId);
        
        if (task) {
          if (task.status === 'completed') {
            // 任务完成，停止轮询
            if (logExtractionPollingIntervalRef.current) {
              clearInterval(logExtractionPollingIntervalRef.current);
              logExtractionPollingIntervalRef.current = null;
            }
            setExtractingLog(false);
            setLogExtractionTaskId(null);

            // 从任务结果中获取日志内容
            if (task.result_data) {
              try {
                const resultData = typeof task.result_data === 'string' 
                  ? JSON.parse(task.result_data) 
                  : task.result_data;
                
                if (resultData.log_content) {
                  setExtractedLogContent(resultData.log_content);
                  setExtractedLogDialogOpen(true);
                } else {
                  setError(null);
                  // 显示成功提示
                  alert('项目日志提取并保存成功！');
                }
              } catch (parseError) {
                console.error('解析任务结果失败:', parseError);
                setError(null);
                alert('项目日志提取并保存成功！');
              }
            } else {
              setError(null);
              alert('项目日志提取并保存成功！');
            }
          } else if (task.status === 'failed') {
            // 任务失败，停止轮询
            if (logExtractionPollingIntervalRef.current) {
              clearInterval(logExtractionPollingIntervalRef.current);
              logExtractionPollingIntervalRef.current = null;
            }
            setExtractingLog(false);
            setLogExtractionTaskId(null);
            setError(task.error_message || '项目日志提取失败');
          }
          // running 和 pending 状态继续轮询
        } else {
          // 任务不存在，可能是刚创建还未同步，继续轮询
        }
      }
    } catch (err: any) {
      console.error('检查日志提取任务状态失败:', err);
      // 不显示错误，继续轮询
    }
  }, [projectId]);

  // 组件卸载时清除轮询
  useEffect(() => {
    return () => {
      if (logExtractionPollingIntervalRef.current) {
        clearInterval(logExtractionPollingIntervalRef.current);
      }
    };
  }, []);

  const handleExtractConfirmed = async () => {
    if (!projectId) return;

    setExtractingLog(true);
    setExtractConfirmDialogOpen(false);

    try {
      console.log('开始发送提取日志请求');
      const response = await apiClient.projectLogs.extract(projectId);

      if (response.success) {
        console.log('提取日志请求成功，任务ID:', response.data?.task_id);

        // 立即设置一个临时的任务状态表示正在处理
        const tempTaskStatus = {
          task_id: response.data?.task_id || 'temp-' + Date.now(),
          status: 'running',
          progress: 0,
          progress_message: '正在启动日志提取任务...',
          created_at: new Date().toISOString()
        };

        setLogExtractionTaskStatus(tempTaskStatus);
        console.log('已设置临时任务状态:', tempTaskStatus);

        // 立即重置extractingLog状态，这样按钮就会显示任务状态而不是"正在发送请求"
        setExtractingLog(false);

        // 然后立即检查真实的任务状态
        setTimeout(() => {
          fetchLogExtractionTaskStatus();
        }, 500); // 缩短延迟时间，更快获取真实状态
      } else {
        console.error('提取日志请求失败:', response.message);
        setExtractingLog(false);
      }
    } catch (err) {
      console.error('提取项目日志失败:', err);
      setExtractingLog(false);
    }
  };

  const handleViewProjectLogs = async () => {
    if (!projectId) return;

    setLoadingLogs(true);
    try {
      const response = await apiClient.projectLogs.getList(projectId);
      if (response.success) {
        if (response.data?.found) {
          // 直接设置content内容，不再使用列表
          setProjectLogs(response.data.log.content || '无日志内容');
        } else {
          setProjectLogs('该项目暂无日志记录');
        }
        setLogsDialogOpen(true);
      }
    } catch (err) {
      console.error('获取项目日志失败:', err);
      setProjectLogs('获取项目日志时发生错误');
      setLogsDialogOpen(true);
    } finally {
      setLoadingLogs(false);
    }
  };

  // 批量操作相关函数
  const handleSelectAll = (event: React.ChangeEvent<HTMLInputElement>) => {
    const checked = event.target.checked;
    if (checked) {
      const allIds = new Set(safeDocuments.map(doc => doc.id));
      setSelectedDocuments(allIds);
    } else {
      setSelectedDocuments(new Set());
    }
  };

  const handleSelectDocument = (documentId: number) => {
    const newSelected = new Set(selectedDocuments);
    if (newSelected.has(documentId)) {
      newSelected.delete(documentId);
    } else {
      newSelected.add(documentId);
    }
    setSelectedDocuments(newSelected);
  };

  const handleBatchMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setBatchMenuAnchorEl(event.currentTarget);
  };

  const handleBatchMenuClose = () => {
    setBatchMenuAnchorEl(null);
  };

  const handleBatchDelete = async () => {
    if (!projectId || selectedDocuments.size === 0) return;

    setBatchProcessing(true);
    try {
      const documentIds = Array.from(selectedDocuments).map(id => id.toString());
      const response = await apiClient.documents.batchDelete(projectId, documentIds);

      if (response.success) {
        // 刷新文档列表
        await fetchDocuments();
        setSelectedDocuments(new Set());

        // 显示成功消息
        const successCount = response.data?.deleted_count || 0;
        setError(null);
      } else {
        setError(response.message || '批量删除失败');
      }
    } catch (err) {
      console.error('批量删除失败:', err);
      setError('批量删除失败，请稍后重试');
    } finally {
      setBatchProcessing(false);
      setBatchDeleteDialogOpen(false);
      handleBatchMenuClose();
    }
  };

  const handleBatchReprocess = async () => {
    if (!projectId || selectedDocuments.size === 0) return;

    setBatchProcessing(true);
    try {
      const documentIds = Array.from(selectedDocuments).map(id => id.toString());
      const response = await apiClient.documents.batchReprocess(projectId, documentIds);

      if (response.success) {
        // 刷新文档列表
        await fetchDocuments();
        setSelectedDocuments(new Set());

        // 显示成功消息
        const successCount = response.data?.success_count || 0;
        setError(null);
      } else {
        setError(response.message || '批量重新处理失败');
      }
    } catch (err) {
      console.error('批量重新处理失败:', err);
      setError('批量重新处理失败，请稍后重试');
    } finally {
      setBatchProcessing(false);
      setBatchReprocessDialogOpen(false);
      handleBatchMenuClose();
    }
  };

  // 获取文档转换任务状态
  const handleGetTaskStatus = async () => {
    if (!projectId) return;

    setLoadingTaskStatus(true);
    try {
      const response = await apiClient.documentTasks.getStatus(projectId);
      if (response.success) {
        setTaskStatusData(response.data);

        // 更新文档级别的任务状态映射
        if (response.data.tasks) {
          const docStatusMap = getLatestTaskStatusForDocuments(response.data.tasks);
          setDocumentTaskStatus(docStatusMap);
        }

        setTaskStatusDialogOpen(true);
      } else {
        setError(response.message || '获取任务状态失败');
      }
    } catch (error: any) {
      console.error('获取任务状态失败:', error);
      setError(error.message || '获取任务状态失败');
    } finally {
      setLoadingTaskStatus(false);
    }
  };

  // 打开上传弹框
  const handleUploadDialogOpen = () => {
    setUploadDialogOpen(true);
  };

  // 关闭上传弹框
  const handleUploadDialogClose = async () => {
    setUploadDialogOpen(false);
    // 关闭弹窗时也刷新文档列表和任务状态
    try {
      await fetchDocuments(true); // 跳过loading状态以避免UI闪烁
      await fetchDocumentTaskStatus();
      console.log('Upload dialog closed, documents refreshed');
    } catch (error) {
      console.error('Error refreshing documents after dialog close:', error);
    }
  };

  // cognee重建处理函数
  const handleRAGSync = async () => {
    if (!projectId) return;

    // 显示确认对话框
    setRagConfirmDialogOpen(true);
  };

  const handleRAGSyncConfirmed = async () => {
    if (!projectId) return;

    setSyncingRAG(true);
    setRagConfirmDialogOpen(false);

    try {
      console.log('开始cognee重建');
      const response = await apiClient.projects.ragIncrementalUpdate(projectId);

      if (response.success) {
        console.log('cognee重建成功:', response.data);

        // 设置重建成功状态
        setRagSyncStatus({
          success: true,
          operations: response.data?.operations_performed || [],
          changes: response.data?.changes_detected || {},
          timestamp: response.data?.timestamp
        });

        // 显示成功消息
        setError(null);

        // 3秒后清除状态
        setTimeout(() => {
          setRagSyncStatus(null);
        }, 3000);
      } else {
        console.error('cognee重建失败:', response.message);
        setError(response.message || 'cognee重建失败');
        setRagSyncStatus({
          success: false,
          error: response.message || 'cognee重建失败'
        });
      }
    } catch (err: any) {
      console.error('cognee重建异常:', err);
      const errorMessage = err.response?.data?.detail || err.message || 'cognee重建发生异常';
      setError(errorMessage);
      setRagSyncStatus({
        success: false,
        error: errorMessage
      });
    } finally {
      setSyncingRAG(false);
    }
  };

  // 处理图数据库可视化
  const handleGraphVisualization = async () => {
    if (!projectId) return;

    setLoadingGraphVisualization(true);
    try {
      console.log('开始生成图数据库可视化');
      const response = await apiClient.get(`/projects/${projectId}/knowledge-graph/visualize`);

      if (response.success) {
        console.log('图数据库可视化生成成功');
        if (response.data.html_content && response.data.html_content.trim()) {
          setGraphVisualizationHtml(response.data.html_content);
          setGraphVisualizationDialogOpen(true);
        } else {
          console.error('HTML内容为空，无法显示可视化');
          setError('返回的可视化HTML内容为空');
        }
      } else {
        console.error('图数据库可视化生成失败:', response.message);
        setError(response.message || '图数据库可视化生成失败');
      }
    } catch (err: any) {
      console.error('图数据库可视化异常:', err);
      const errorMessage = err.response?.data?.detail || err.message || '图数据库可视化发生异常';
      setError(errorMessage);
    } finally {
      setLoadingGraphVisualization(false);
    }
  };

  // 页面加载时获取任务状态
  const fetchDocumentTaskStatus = useCallback(async () => {
    if (!projectId) return;

    setLoadingInitialTaskStatus(true);
    try {
      const response = await apiClient.documentTasks.getStatus(projectId);
      if (response.success) {
        // 更新文档级别的任务状态映射
        if (response.data.tasks) {
          const newDocStatusMap = getLatestTaskStatusForDocuments(response.data.tasks);

          // 使用函数式更新来获取当前状态，避免循环依赖
          setDocumentTaskStatus(currentStatus => {
            // 只有当状态真正发生变化时才更新
            let hasChanges = false;
            const currentKeys = Object.keys(currentStatus);
            const newKeys = Object.keys(newDocStatusMap);

            // 检查键数量是否不同
            if (currentKeys.length !== newKeys.length) {
              hasChanges = true;
            } else {
              // 检查每个文档的状态是否发生变化
              for (const docId of newKeys) {
                const docIdNum = parseInt(docId);
                if (!isTaskStatusEqual(currentStatus[docIdNum], newDocStatusMap[docIdNum])) {
                  hasChanges = true;
                  break;
                }
              }
            }

            if (hasChanges) {
              console.log('检测到任务状态变化，更新状态');
              return newDocStatusMap;
            } else {
              console.log('任务状态无变化，跳过更新');
              return currentStatus;
            }
          });
        }
      }
    } catch (error: any) {
      console.error('获取初始任务状态失败:', error);
      // 静默失败，不影响页面显示
    } finally {
      setLoadingInitialTaskStatus(false);
    }
  }, [projectId, getLatestTaskStatusForDocuments, isTaskStatusEqual]);

  // 上传完成回调
  const handleUploadComplete = useCallback(async () => {
    console.log('文档上传完成，开始刷新文档列表');
    let retryCount = 0;
    const maxRetries = 3;

    const tryRefresh = async (): Promise<boolean> => {
      try {
        // 先等待一小段时间确保后端处理完成
        await new Promise(resolve => setTimeout(resolve, 800));

        // 直接刷新当前页面的文档列表
        await fetchDocuments(true); // 跳过loading状态，避免UI闪烁

        return true;
      } catch (error) {
        console.error('刷新文档列表失败:', error);
        return false;
      }
    };

    // 重试机制
    let success = false;
    while (retryCount < maxRetries && !success) {
      success = await tryRefresh();
      if (!success) {
        retryCount++;
        console.log(`第 ${retryCount} 次重试刷新文档列表`);
        await new Promise(resolve => setTimeout(resolve, 1000 * retryCount)); // 递增延迟
      }
    }

    try {
      // 延迟刷新任务状态，确保文档列表已更新
      setTimeout(async () => {
        await fetchDocumentTaskStatus();
        console.log('任务状态刷新完成');
      }, 1000);

      // 触发全局文档刷新事件（供其他页面使用）
      window.dispatchEvent(new CustomEvent('documentsShouldRefresh'));
    } catch (error) {
      console.error('刷新任务状态失败:', error);
    }

    setUploadDialogOpen(false);
  }, [fetchDocuments, fetchDocumentTaskStatus]);

  // 检查日志提取任务状态
  const fetchLogExtractionTaskStatus = async () => {
    if (!projectId) return;

    setLoadingLogTaskStatus(true);
    try {
      console.log('获取日志提取任务状态...');
      const response = await apiClient.projectLogs.getTasks(projectId);

      if (response.success && response.data?.tasks) {
        console.log('获取到任务列表:', response.data.tasks);

        // 检查是否有正在运行或等待中的日志提取任务
        const activeTasks = response.data.tasks.filter((task: any) =>
          task.status === 'running' || task.status === 'pending'
        );

        console.log('活跃任务数量:', activeTasks.length);

        // 按创建时间排序，获取最新的活跃任务
        if (activeTasks.length > 0) {
          const latestActiveTask = activeTasks.sort((a: any, b: any) => {
            const timeA = new Date(a.created_at || 0).getTime();
            const timeB = new Date(b.created_at || 0).getTime();
            return timeB - timeA;
          })[0];

          console.log('设置最新活跃任务:', latestActiveTask);
          setLogExtractionTaskStatus(latestActiveTask);
        } else {
          console.log('没有活跃任务，清空任务状态');
          setLogExtractionTaskStatus(null);
        }
      } else {
        console.log('没有任务数据或获取失败');
        setLogExtractionTaskStatus(null);
      }
    } catch (error: any) {
      console.error('获取日志提取任务状态失败:', error);
      // 如果有临时任务状态，不要立即清空，给用户一个更好的体验
      if (!logExtractionTaskStatus?.task_id?.startsWith('temp-')) {
        setLogExtractionTaskStatus(null);
      }
    } finally {
      setLoadingLogTaskStatus(false);
    }
  };

  // 检查是否有正在运行的日志提取任务
  const hasActiveLogExtractionTask = () => {
    return logExtractionTaskStatus && (
      logExtractionTaskStatus.status === 'running' ||
      logExtractionTaskStatus.status === 'pending'
    );
  };

  // 获取按钮状态文本
  const getLogExtractionButtonText = () => {
    if (extractingLog) return '正在发送请求...';

    if (hasActiveLogExtractionTask()) {
      const task = logExtractionTaskStatus;
      if (task.status === 'running') {
        return task.progress_message || '正在提取日志...';
      }
      if (task.status === 'pending') {
        return task.progress_message || '等待处理...';
      }
    }

    return '提取项目日志';
  };

  // 判断按钮是否应该被禁用
  const isLogExtractionButtonDisabled = () => {
    return loading || extractingLog || safeDocuments.length === 0 || hasActiveLogExtractionTask();
  };

  // 定期检查活跃的日志提取任务状态
  useEffect(() => {
    if (!hasActiveLogExtractionTask()) {
      return;
    }

    console.log('启动日志提取任务状态定时更新');
    const interval = setInterval(() => {
      fetchLogExtractionTaskStatus();
    }, 3000); // 减少到3秒，提高响应速度

    return () => {
      clearInterval(interval);
      console.log('停止日志提取任务状态定时更新');
    };
  }, [logExtractionTaskStatus?.status]);

  // 监听任务状态变化，当任务完成时重置extractingLog状态
  useEffect(() => {
    if (!extractingLog) return;

    const task = logExtractionTaskStatus;
    if (task) {
      // 当任务完成、失败或取消时，重置extractingLog状态
      if (task.status === 'completed' || task.status === 'failed' || task.status === 'cancelled') {
        console.log(`任务状态变更为 ${task.status}，重置extractingLog状态`);
        setExtractingLog(false);
      }
    }
  }, [logExtractionTaskStatus, extractingLog]);

  useEffect(() => {
    fetchDocuments();
    fetchDocumentTaskStatus();
    fetchLogExtractionTaskStatus();
  }, [projectId]);

  // 监听文档刷新事件
  useEffect(() => {
    const handleRefresh = () => {
      fetchDocuments();
      fetchDocumentTaskStatus();
      fetchLogExtractionTaskStatus();
    };
    window.addEventListener('documentsShouldRefresh', handleRefresh);
    return () => {
      window.removeEventListener('documentsShouldRefresh', handleRefresh);
    };
  }, [projectId]);

  // 更新indeterminate状态
  useEffect(() => {
    const selectedCount = selectedDocuments.size;
    const totalCount = safeDocuments.length;

    if (selectedCount === 0) {
      setIndeterminate(false);
    } else if (selectedCount === totalCount) {
      setIndeterminate(false);
    } else {
      setIndeterminate(true);
    }
  }, [selectedDocuments, safeDocuments]);

  // 添加防抖定时器引用
  const updateTimerRef = useRef<NodeJS.Timeout | null>(null);
  const lastUpdateRef = useRef<number>(0);

  // 当有等待或运行中的文档任务时，定时更新任务状态
  useEffect(() => {
    // 检查是否有正在运行或等待中的文档任务
    const hasActiveDocumentTasks = () => {
      const taskStatuses = Object.values(documentTaskStatus);
      return taskStatuses.some(status =>
        status?.status === 'running' ||
        status?.status === 'pending'
      );
    };

    // 如果没有活动任务，不启动定时器
    if (!hasActiveDocumentTasks()) {
      return;
    }

    // 设置定时器，每5秒更新一次文档任务状态（降低频率）
    const interval = setInterval(async () => {
      const now = Date.now();

      // 防抖：距离上次更新不足3秒时跳过
      if (now - lastUpdateRef.current < 3000) {
        console.log('距离上次更新时间过短，跳过本次更新');
        return;
      }

      // 清除之前的防抖定时器
      if (updateTimerRef.current) {
        clearTimeout(updateTimerRef.current);
      }

      // 设置新的防抖定时器
      updateTimerRef.current = setTimeout(async () => {
        try {
          await fetchDocumentTaskStatus();
          lastUpdateRef.current = Date.now();
          console.log('定时更新文档任务状态完成');
        } catch (error) {
          console.error('定时更新文档任务状态失败:', error);
        }
      }, 500); // 延迟500ms执行，进一步减少频繁更新
    }, 5000); // 每5秒检查一次

    console.log('启动文档任务状态定时更新（优化版本）');

    return () => {
      clearInterval(interval);
      if (updateTimerRef.current) {
        clearTimeout(updateTimerRef.current);
      }
      console.log('停止文档任务状态定时更新');
    };
  }, [documentTaskStatus, projectId, fetchDocumentTaskStatus]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Navbar />
      <Box sx={{ mt: 3, mb: 4, px: 3 }}>
        <Typography variant="h4" sx={{ mb: 3 }}>
          项目文档
        </Typography>

        {/* 控制按钮 */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            {selectedDocuments.size > 0 && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="body2" color="text.secondary">
                  已选择 {selectedDocuments.size} 个文档
                </Typography>
                <Button
                  variant="contained"
                  size="small"
                  startIcon={<DeleteIcon />}
                  onClick={() => setBatchDeleteDialogOpen(true)}
                  disabled={batchProcessing}
                  color="error"
                >
                  批量删除
                </Button>
                <Button
                  variant="contained"
                  size="small"
                  startIcon={<RefreshIcon />}
                  onClick={() => setBatchReprocessDialogOpen(true)}
                  disabled={batchProcessing}
                  color="secondary"
                >
                  重新处理
                </Button>
              </Box>
            )}
          </Box>
          <Box>
            <Button
              variant="contained"
              startIcon={<StorageIcon />}
              onClick={handleUploadDialogOpen}
              disabled={loading}
              sx={{ mr: 2 }}
            >
              上传文档
            </Button>
            <Button
              variant="contained"
              startIcon={extractingLog || logExtractionTaskId ? <CircularProgress size={20} /> : <ExtractIcon />}
              onClick={handleExtractProjectLog}
              disabled={loading || extractingLog || logExtractionTaskId !== null || safeDocuments.filter(doc => doc.status === 'uploaded').length === 0}
              sx={{ mr: 2 }}
            >
              {extractingLog || logExtractionTaskId ? '提取中...' : '提取日志'}
            </Button>
            {/* <Button
              variant="contained"
              startIcon={loadingLogs ? <CircularProgress size={20} /> : <ViewLogsIcon />}
              onClick={handleViewProjectLogs}
              disabled={loading || loadingLogs}
              sx={{ mr: 2 }}
            >
              {loadingLogs ? '加载中...' : '查看项目日志'}
            </Button> */}
            <Button
              variant="outlined"
              startIcon={syncingRAG ? <CircularProgress size={20} /> : <CloudSyncIcon />}
              onClick={handleRAGSync}
              disabled={loading || syncingRAG || safeDocuments.length === 0}
              sx={{ mr: 2 }}
            >
              {syncingRAG ? '索引重建中...' : 'cognee重建'}
            </Button>
            <Button
              variant="outlined"
              startIcon={loadingGraphVisualization ? <CircularProgress size={20} /> : <GraphIcon />}
              onClick={handleGraphVisualization}
              disabled={loading || loadingGraphVisualization}
              sx={{ mr: 2 }}
            >
              {loadingGraphVisualization ? '生成中...' : '图谱预览'}
            </Button>
            {/* <Button
              variant="contained"
              startIcon={loadingTaskStatus ? <CircularProgress size={20} /> : <TaskStatusIcon />}
              onClick={handleGetTaskStatus}
              disabled={loading || loadingTaskStatus}
            >
              {loadingTaskStatus ? '加载中...' : '任务状态'}
            </Button> */}
          </Box>
        </Box>

      {/* 错误提示 */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* cognee重建状态提示 */}
      {ragSyncStatus && (
        <Alert
          severity={ragSyncStatus.success ? "success" : "error"}
          sx={{ mb: 3 }}
          onClose={() => setRagSyncStatus(null)}
        >
          {ragSyncStatus.success ? (
            <Box>
              <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1 }}>
                ✅ cognee重建成功
              </Typography>
              {ragSyncStatus.operations && ragSyncStatus.operations.length > 0 && (
                <Typography variant="body2">
                  执行操作: {ragSyncStatus.operations.join(', ')}
                </Typography>
              )}
              <Box sx={{ mt: 1 }}>
                <Typography variant="caption" color="text.secondary">
                  找到MD文件: {ragSyncStatus.found_files_count || 0} |
                  成功读取: {ragSyncStatus.successful_reads || 0} |
                </Typography>
              </Box>
              {ragSyncStatus.dataset_name && (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                  数据集: {ragSyncStatus.dataset_name}
                </Typography>
              )}
            </Box>
          ) : (
            <Typography variant="body2">
              ❌ {ragSyncStatus.error}
            </Typography>
          )}
        </Alert>
      )}

      {/* 文档统计 */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            <Box>
              <Typography variant="h6" color="primary">
                {safeDocuments.length}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                总文档数
              </Typography>
            </Box>
            <Box>
              <Typography variant="h6" color="success.main">
                {safeDocuments.filter(doc => doc.status === 'uploaded').length}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                已上传
              </Typography>
            </Box>
            <Box>
              <Typography variant="h6" color="warning.main">
                {safeDocuments.filter(doc => doc.status === 'processing').length}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                处理中
              </Typography>
            </Box>
            <Box>
              <Typography variant="h6" color="error.main">
                {formatFileSize(safeDocuments.reduce((total, doc) => total + doc.file_size, 0))}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                总大小
              </Typography>
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* 文档列表 */}
      {safeDocuments.length === 0 ? (
        <Card>
          <CardContent sx={{ textAlign: 'center', py: 8 }}>
            <FileIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
            <Typography variant="h6" color="text.secondary">
              暂无文档
            </Typography>
            <Typography variant="body2" color="text.secondary">
              请使用右上角的文件上传功能上传文档
            </Typography>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox">
                    <Checkbox
                      indeterminate={indeterminate}
                      checked={safeDocuments.length > 0 && selectedDocuments.size === safeDocuments.length}
                      onChange={handleSelectAll}
                      disabled={loading}
                    />
                  </TableCell>
                  <TableCell>文件名</TableCell>
                  <TableCell>类型</TableCell>
                  <TableCell>大小</TableCell>
                  <TableCell>状态</TableCell>
                  <TableCell>任务状态</TableCell>
                  <TableCell>上传时间</TableCell>
                  <TableCell align="right">操作</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {safeDocuments.map((document) => (
                  <DocumentTableRow
                    key={document.id}
                    document={document}
                    isSelected={selectedDocuments.has(document.id)}
                    onSelectDocument={handleSelectDocument}
                    onDownload={handleDownload}
                    onViewMarkdown={handleViewMarkdown}
                    onDeleteClick={handleDeleteClick}
                    taskStatus={documentTaskStatus[document.id]}
                  />
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Card>
      )}

      {/* 删除确认对话框 */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>确认删除</DialogTitle>
        <DialogContent>
          <Typography>
            确定要删除文档 "{selectedDocument?.document_name}" 吗？此操作无法撤销。
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            删除后将同时移除原始文件、数据库记录和所有相关的Markdown文件。
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>
            取消
          </Button>
          <Button onClick={handleDelete} color="error" variant="contained">
            删除
          </Button>
        </DialogActions>
      </Dialog>

      {/* Markdown内容查看对话框 */}
      <Dialog
        open={markdownDialogOpen}
        onClose={() => setMarkdownDialogOpen(false)}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: { minHeight: '70vh' }
        }}
      >
        <DialogTitle>
          <Typography variant="h6">
            {selectedDocument?.document_name} - Markdown内容
          </Typography>
        </DialogTitle>
        <DialogContent>
          {loadingMarkdown ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '200px' }}>
              <CircularProgress />
              <Typography sx={{ ml: 2 }}>加载中...</Typography>
            </Box>
          ) : (
            <Box sx={{
              maxHeight: '60vh',
              overflow: 'auto',
              backgroundColor: '#ffffff',
              p: 3,
              borderRadius: 1,
              '& img': {
                maxWidth: '100%',
                height: 'auto'
              },
              '& code': {
                backgroundColor: '#f5f5f5',
                padding: '2px 4px',
                borderRadius: '3px',
                fontSize: '0.9em'
              },
              '& pre': {
                backgroundColor: '#f5f5f5',
                padding: '16px',
                borderRadius: '4px',
                overflow: 'auto',
                '& code': {
                  backgroundColor: 'transparent',
                  padding: 0
                }
              }
            }}>
              <ReactMarkdown
                remarkPlugins={[remarkGfm]}
                components={{
                  h1: ({ children }) => <Typography variant="h4" sx={{ mt: 3, mb: 2, color: 'primary.main' }}>{children}</Typography>,
                  h2: ({ children }) => <Typography variant="h5" sx={{ mt: 2, mb: 2, color: 'primary.main' }}>{children}</Typography>,
                  h3: ({ children }) => <Typography variant="h6" sx={{ mt: 2, mb: 1, color: 'primary.main' }}>{children}</Typography>,
                  p: ({ children }) => <Typography variant="body1" sx={{ mb: 2, lineHeight: 1.6 }}>{children}</Typography>,
                  ul: ({ children }) => <Box component="ul" sx={{ pl: 2, mb: 2 }}>{children}</Box>,
                  ol: ({ children }) => <Box component="ol" sx={{ pl: 2, mb: 2 }}>{children}</Box>,
                  li: ({ children }) => <Typography component="li" variant="body1" sx={{ mb: 1 }}>{children}</Typography>,
                  blockquote: ({ children }) => (
                    <Box sx={{
                      borderLeft: 4,
                      borderColor: 'grey.300',
                      pl: 2,
                      py: 1,
                      my: 2,
                      backgroundColor: 'grey.50',
                      fontStyle: 'italic'
                    }}>
                      <Typography variant="body2" color="text.secondary">{children}</Typography>
                    </Box>
                  ),
                  code: ({ inline, children }: any) => {
                    if (inline) {
                      return (
                        <Box component="code" sx={{
                          backgroundColor: 'grey.100',
                          px: 1,
                          py: 0.5,
                          borderRadius: 0.5,
                          fontFamily: 'monospace',
                          fontSize: '0.9em'
                        }}>
                          {children}
                        </Box>
                      );
                    }
                    return (
                      <Box component="pre" sx={{
                        backgroundColor: 'grey.900',
                        color: 'grey.100',
                        p: 2,
                        borderRadius: 1,
                        overflow: 'auto',
                        my: 2,
                        fontFamily: 'monospace',
                        fontSize: '0.875rem',
                        lineHeight: 1.5
                      }}>
                        {children}
                      </Box>
                    );
                  },
                  table: ({ children }) => (
                    <Box sx={{ overflow: 'auto', my: 2 }}>
                      <table style={{ minWidth: '100%', borderCollapse: 'collapse' }}>
                        {children}
                      </table>
                    </Box>
                  ),
                  th: ({ children }) => (
                    <Box component="th" sx={{
                      border: '1px solid',
                      borderColor: 'grey.300',
                      backgroundColor: 'grey.100',
                      px: 2,
                      py: 1,
                      textAlign: 'left',
                      fontWeight: 'bold'
                    }}>
                      {children}
                    </Box>
                  ),
                  td: ({ children }) => (
                    <Box component="td" sx={{
                      border: '1px solid',
                      borderColor: 'grey.300',
                      px: 2,
                      py: 1
                    }}>
                      {children}
                    </Box>
                  )
                }}
              >
                {markdownContent}
              </ReactMarkdown>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setMarkdownDialogOpen(false)}>
            关闭
          </Button>
          {markdownContent && (
            <Button
              variant="contained"
              onClick={() => {
                const blob = new Blob([markdownContent], { type: 'text/markdown' });
                const url = URL.createObjectURL(blob);

                if (typeof document !== 'undefined' && document.createElement) {
                  const link = document.createElement('a');
                  link.href = url;
                  link.download = `${selectedDocument?.document_name}.md`;
                  document.body.appendChild(link);
                  link.click();
                  document.body.removeChild(link);
                  URL.revokeObjectURL(url);
                } else {
                  window.open(url, '_blank');
                  setTimeout(() => URL.revokeObjectURL(url), 100);
                }
              }}
            >
              下载Markdown文件
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* 提取项目日志确认对话框 */}
      <Dialog
        open={extractConfirmDialogOpen}
        onClose={() => setExtractConfirmDialogOpen(false)}
        fullWidth
        maxWidth="sm"
        PaperProps={{
          sx: { borderRadius: 2 }
        }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Typography variant="h6" color="warning.main">
            确认提取项目日志
          </Typography>
        </DialogTitle>
        <DialogContent dividers sx={{ paddingTop: '16px !important' }}>
          <Box sx={{ py: 1 }}>
            <Typography variant="body1" color="text.secondary">
              确定要提取项目日志吗？此操作会覆盖原来的日志内容，无法撤销。
            </Typography>
            <Alert
              severity="warning"
              sx={{ mt: 2 }}
              iconMapping={{ warning: <AutoAwesomeIcon /> }}
            >
              <Typography variant="body2">
                提取操作将基于项目文档重新生成项目概要分析报告
              </Typography>
            </Alert>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button
            onClick={() => setExtractConfirmDialogOpen(false)}
            variant="outlined"
            size="medium"
            disabled={extractingLog}
          >
            取消
          </Button>
          <Button
            onClick={handleExtractConfirmed}
            variant="contained"
            color="warning"
            size="medium"
            disabled={extractingLog}
            startIcon={extractingLog ? <CircularProgress size={20} /> : <AutoAwesomeIcon />}
          >
            {extractingLog ? '提取中...' : '确认提取'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* 项目日志查看对话框 */}
      <Dialog
        open={logsDialogOpen}
        onClose={() => setLogsDialogOpen(false)}
        fullWidth
        maxWidth="lg"
        PaperProps={{
          sx: {
            minHeight: '70vh',
            borderRadius: 2
          }
        }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Typography variant="h6" color="primary" fontWeight="medium">
            项目日志
          </Typography>
        </DialogTitle>
        <DialogContent dividers sx={{ paddingTop: '16px !important' }}>
          {loadingLogs ? (
            <Box sx={{
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'center',
              minHeight: '300px',
              gap: 2
            }}>
              <CircularProgress color="primary" />
              <Typography variant="body1" color="text.secondary">
                正在加载项目日志...
              </Typography>
            </Box>
          ) : (
            <Box sx={{
              minHeight: '300px',
              maxHeight: '50vh',
              overflow: 'auto'
            }}>
              <Paper
                elevation={0}
                sx={{
                  backgroundColor: '#fafafa',
                  border: '1px solid #e0e0e0',
                  borderRadius: 1,
                  p: 3,
                  fontFamily: '"Roboto Mono", "Courier New", monospace',
                  fontSize: '0.875rem',
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word',
                  lineHeight: 1.6,
                  color: '#333'
                }}
              >
                {/* 转义HTML标签，避免React将其解析为组件 */}
                {projectLogs
                  ? projectLogs.replace(/</g, '&lt;').replace(/>/g, '&gt;')
                  : '该项目暂无日志内容'
                }
              </Paper>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button
            onClick={() => setLogsDialogOpen(false)}
            variant="outlined"
            size="medium"
          >
            关闭
          </Button>
        </DialogActions>
      </Dialog>

      {/* 批量删除确认对话框 */}
      <Dialog
        open={batchDeleteDialogOpen}
        onClose={() => setBatchDeleteDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>确认批量删除</DialogTitle>
        <DialogContent>
          <Typography>
            确定要删除选中的 {selectedDocuments.size} 个文档吗？此操作无法撤销。
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            删除后将同时移除：
          </Typography>
          <Box component="ul" sx={{ mt: 1, pl: 2, color: 'text.secondary' }}>
            <Typography component="li" variant="body2">
              • 原始文档文件
            </Typography>
            <Typography component="li" variant="body2">
              • 数据库中的文档记录
            </Typography>
            <Typography component="li" variant="body2">
              • 转换后的Markdown文件（包括项目doc目录中的相关文件）
            </Typography>
          </Box>
          <Typography variant="body2" color="warning.main" sx={{ mt: 2, fontWeight: 500 }}>
            ⚠️ 请注意：此操作不可恢复，请谨慎操作！
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBatchDeleteDialogOpen(false)} disabled={batchProcessing}>
            取消
          </Button>
          <Button
            onClick={handleBatchDelete}
            color="error"
            variant="contained"
            disabled={batchProcessing}
            startIcon={batchProcessing ? <CircularProgress size={20} /> : <DeleteIcon />}
          >
            {batchProcessing ? '删除中...' : '确认删除'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* 批量重新处理确认对话框 */}
      <Dialog
        open={batchReprocessDialogOpen}
        onClose={() => setBatchReprocessDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>确认批量重新处理</DialogTitle>
        <DialogContent>
          <Typography>
            确定要重新处理选中的 {selectedDocuments.size} 个文档吗？
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            这将重新启动文档的MD转换处理任务，覆盖之前的结果。
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBatchReprocessDialogOpen(false)} disabled={batchProcessing}>
            取消
          </Button>
          <Button
            onClick={handleBatchReprocess}
            color="secondary"
            variant="contained"
            disabled={batchProcessing}
            startIcon={batchProcessing ? <CircularProgress size={20} /> : <RefreshIcon />}
          >
            {batchProcessing ? '处理中...' : '确认处理'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* 任务状态对话框 */}
      <Dialog
        open={taskStatusDialogOpen}
        onClose={() => setTaskStatusDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>文档转换任务状态</DialogTitle>
        <DialogContent>
          {taskStatusData ? (
            <Box>
              {/* 任务摘要 */}
              <Box sx={{ mb: 3 }}>
                <Typography variant="h6" gutterBottom>
                  任务摘要
                </Typography>
                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                  <Chip label={`总计: ${taskStatusData.total_tasks}`} color="default" />
                  <Chip label={`等待中: ${taskStatusData.summary.pending}`} color="default" />
                  <Chip label={`运行中: ${taskStatusData.summary.running}`} color="info" />
                  <Chip label={`已完成: ${taskStatusData.summary.completed}`} color="success" />
                  <Chip label={`失败: ${taskStatusData.summary.failed}`} color="error" />
                  <Chip label={`已取消: ${taskStatusData.summary.cancelled}`} color="default" />
                </Box>
              </Box>

              {/* 任务列表 */}
              {taskStatusData.tasks && taskStatusData.tasks.length > 0 && (
                <Box>
                  <Typography variant="h6" gutterBottom>
                    最近任务 (最多显示20个)
                  </Typography>
                  <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>任务ID</TableCell>
                          <TableCell>文档</TableCell>
                          <TableCell>状态</TableCell>
                          <TableCell>进度</TableCell>
                          <TableCell>创建时间</TableCell>
                          <TableCell>完成时间</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {taskStatusData.tasks.map((task: any) => (
                          <TableRow key={task.task_id}>
                            <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.8em' }}>
                              {task.task_id}
                            </TableCell>
                            <TableCell>
                              {taskStatusData.document_info[task.document_id] ? (
                                <Box>
                                  <Typography variant="body2">
                                    {taskStatusData.document_info[task.document_id].document_name}
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary">
                                    ID: {task.document_id}
                                  </Typography>
                                </Box>
                              ) : (
                                <Typography variant="body2" color="text.secondary">
                                  文档ID: {task.document_id}
                                </Typography>
                              )}
                            </TableCell>
                            <TableCell>
                              <Chip
                                label={task.status}
                                color={
                                  task.status === 'completed' ? 'success' :
                                  task.status === 'running' ? 'info' :
                                  task.status === 'failed' ? 'error' :
                                  task.status === 'pending' ? 'default' :
                                  'default'
                                }
                                size="small"
                              />
                            </TableCell>
                            <TableCell>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Typography variant="body2">
                                  {task.progress}%
                                </Typography>
                                {task.status === 'running' && (
                                  <CircularProgress size={16} />
                                )}
                              </Box>
                              {task.progress_message && (
                                <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                                  {task.progress_message}
                                </Typography>
                              )}
                            </TableCell>
                            <TableCell>
                              {task.created_at ? (
                                <Typography variant="caption">
                                  {new Date(task.created_at).toLocaleString('zh-CN')}
                                </Typography>
                              ) : (
                                '-'
                              )}
                            </TableCell>
                            <TableCell>
                              {task.completed_at ? (
                                <Typography variant="caption">
                                  {new Date(task.completed_at).toLocaleString('zh-CN')}
                                </Typography>
                              ) : (
                                '-'
                              )}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              )}

              {taskStatusData.tasks && taskStatusData.tasks.length === 0 && (
                <Box sx={{ textAlign: 'center', py: 3 }}>
                  <Typography variant="body2" color="text.secondary">
                    暂无文档转换任务
                  </Typography>
                </Box>
              )}
            </Box>
          ) : (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
              <CircularProgress />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTaskStatusDialogOpen(false)}>
            关闭
          </Button>
        </DialogActions>
      </Dialog>

      {/* cognee重建确认对话框 */}
      <Dialog
        open={ragConfirmDialogOpen}
        onClose={() => setRagConfirmDialogOpen(false)}
        fullWidth
        maxWidth="sm"
        PaperProps={{
          sx: { borderRadius: 2 }
        }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Typography variant="h6" color="primary">
            确认cognee重建
          </Typography>
        </DialogTitle>
        <DialogContent dividers sx={{ paddingTop: '16px !important' }}>
          <Box sx={{ py: 1 }}>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
              确定要执行cognee重建吗？此操作将会：
            </Typography>
            <Box component="ul" sx={{ pl: 2, color: 'text.secondary' }}>
              <Typography component="li" variant="body2" sx={{ mb: 1 }}>
                • 扫描项目中的所有MD文档
              </Typography>
              <Typography component="li" variant="body2" sx={{ mb: 1 }}>
                • 将文档内容添加到Cognee知识库
              </Typography>
              <Typography component="li" variant="body2" sx={{ mb: 1 }}>
                • 使用cognify构建知识图谱和向量索引
              </Typography>
            </Box>
            <Alert
              severity="info"
              sx={{ mt: 2 }}
              iconMapping={{ info: <CloudSyncIcon /> }}
            >
              <Typography variant="body2">
                重建完成后，AI助手将能够基于项目文档内容提供更准确的回答
              </Typography>
            </Alert>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button
            onClick={() => setRagConfirmDialogOpen(false)}
            variant="outlined"
            size="medium"
            disabled={syncingRAG}
          >
            取消
          </Button>
          <Button
            onClick={handleRAGSyncConfirmed}
            variant="contained"
            color="primary"
            size="medium"
            disabled={syncingRAG}
            startIcon={syncingRAG ? <CircularProgress size={20} /> : <CloudSyncIcon />}
          >
            {syncingRAG ? '同步中...' : '确认同步'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* 图数据库可视化对话框 */}
      <Dialog
        open={graphVisualizationDialogOpen}
        onClose={() => setGraphVisualizationDialogOpen(false)}
        maxWidth="xl"
        fullWidth
        PaperProps={{
          sx: {
            height: '90vh',
            maxHeight: '90vh'
          }
        }}
      >
        <DialogTitle sx={{ pb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
          <GraphIcon color="primary" />
          知识图谱可视化
        </DialogTitle>
        <DialogContent dividers sx={{ padding: 0, height: 'calc(90vh - 120px)', overflow: 'hidden' }}>
          {graphVisualizationHtml ? (
            <iframe
              srcDoc={graphVisualizationHtml}
              style={{
                width: '100%',
                height: '100%',
                border: 'none',
                borderRadius: 0
              }}
              title="知识图谱可视化"
            />
          ) : (
            <Box sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              height: '100%',
              flexDirection: 'column',
              gap: 2
            }}>
              <CircularProgress size={48} />
              <Typography variant="body1" color="text.secondary">
                正在生成知识图谱可视化...
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button
            onClick={() => setGraphVisualizationDialogOpen(false)}
            variant="outlined"
          >
            关闭
          </Button>
        </DialogActions>
      </Dialog>

      {/* 文档上传弹框 */}
      <DocumentUploadDialog
        open={uploadDialogOpen}
        onClose={handleUploadDialogClose}
        projectId={projectId || ''}
        onUploadComplete={handleUploadComplete}
      />

      {/* 提取的项目日志对话框 */}
      <Dialog
        open={extractedLogDialogOpen}
        onClose={() => setExtractedLogDialogOpen(false)}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            height: '90vh',
            maxHeight: '90vh'
          }
        }}
      >
        <DialogTitle sx={{ pb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
          <ExtractIcon color="primary" />
          提取的项目日志（已保存）
        </DialogTitle>
        <DialogContent dividers sx={{ padding: 2, height: 'calc(90vh - 120px)', overflow: 'auto' }}>
          <Alert severity="success" sx={{ mb: 2 }}>
            项目日志已成功提取并保存到数据库
          </Alert>
          <Box sx={{ whiteSpace: 'pre-wrap', fontFamily: 'monospace', fontSize: '0.875rem' }}>
            {extractedLogContent || '暂无日志内容'}
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button
            onClick={() => setExtractedLogDialogOpen(false)}
            variant="contained"
            color="primary"
          >
            关闭
          </Button>
        </DialogActions>
      </Dialog>

      {/* 提取日志确认对话框 */}
      <Dialog
        open={extractLogConfirmDialogOpen}
        onClose={() => setExtractLogConfirmDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ pb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
          <ExtractIcon color="warning" />
          确认提取项目日志
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            <Typography variant="body1" sx={{ mb: 2 }}>
              系统将从已上传的项目文档中提取项目日志信息。
            </Typography>
            <Alert severity="warning" sx={{ mt: 2 }}>
              <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1 }}>
                ⚠️ 重要提示
              </Typography>
              <Typography variant="body2">
                提取日志后，将<strong>覆盖</strong>原有的项目日志内容。如果项目中已有日志，原有内容将被替换。
              </Typography>
            </Alert>
            <Typography variant="body2" sx={{ mt: 2, color: 'text.secondary' }}>
              是否继续提取并保存项目日志？
            </Typography>
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button
            onClick={() => setExtractLogConfirmDialogOpen(false)}
            variant="outlined"
            disabled={extractingLog}
          >
            取消
          </Button>
          <Button
            onClick={handleExtractLogConfirmed}
            variant="contained"
            color="primary"
            disabled={extractingLog}
            startIcon={extractingLog ? <CircularProgress size={20} /> : <ExtractIcon />}
          >
            {extractingLog ? '提取中...' : '确认提取'}
          </Button>
        </DialogActions>
      </Dialog>

        </Box>
    </Box>
  );
};

export default DocumentsPage;